import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Chip,
  CircularProgress,
  Paper,
  Checkbox,
  FormControlLabel,
  TextField,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import plantillasApi from '../../../api/plantillasApi';
import configuracionApi from '../../../api/configuracionApi';
import empresasApi from '../../../api/empresasApi';
import usuariosApi from '../../../api/usuariosApi';

/**
 * Dialog para aprobar un negocio pendiente
 * Permite seleccionar plantilla y opcionalmente crear usuario admin
 *
 * @param {Boolean} open - Si el dialog está abierto
 * @param {Object} negocio - Objeto con datos del negocio pendiente
 * @param {Function} onClose - Callback al cerrar
 * @param {Function} onAprobado - Callback cuando se aprueba exitosamente
 */
function DialogAprobarNegocio({ open, negocio, onClose, onAprobado }) {

  // Estados
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('');
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaPreview, setPlantillaPreview] = useState(null);
  const [loadingPlantillas, setLoadingPlantillas] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  // Estados para crear usuario admin
  const [crearUsuarioAdmin, setCrearUsuarioAdmin] = useState(true);
  const [datosAdmin, setDatosAdmin] = useState({
    nombre: '',
    email: '',
    password: ''
  });

  /**
   * Mapeo de IDs de estado a nombres por defecto
   */
  const nombresEstadosDefault = {
    1: 'Nuevo',
    2: 'Pendiente',
    3: 'Confirmado',
    4: 'En Proceso',
    5: 'En Camino',
    6: 'Completado',
    7: 'Cancelado',
    8: 'Reprogramado',
    9: 'En Espera',
    10: 'Finalizado'
  };

  /**
   * Cargar plantillas al abrir el dialog
   */
  useEffect(() => {
    if (open) {
      cargarPlantillas();
      // Pre-llenar email del admin con el correo del negocio
      if (negocio?.correo_contacto) {
        setDatosAdmin(prev => ({
          ...prev,
          email: negocio.correo_contacto
        }));
      }
    }
  }, [open, negocio]);

  /**
   * Actualizar preview cuando cambia plantilla seleccionada
   */
  useEffect(() => {
    if (plantillaSeleccionada) {
      const plantilla = plantillas.find(p => p.id_plantilla === plantillaSeleccionada);
      setPlantillaPreview(plantilla);
    } else {
      setPlantillaPreview(null);
    }
  }, [plantillaSeleccionada, plantillas]);

  /**
   * Cargar plantillas disponibles
   */
  const cargarPlantillas = async () => {
    try {
      setLoadingPlantillas(true);
      const response = await plantillasApi.getPlantillas();
      setPlantillas(response.data || []);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
      setMensaje({
        type: 'error',
        text: 'Error al cargar plantillas disponibles'
      });
    } finally {
      setLoadingPlantillas(false);
    }
  };

  /**
   * Validar datos del formulario
   */
  const validarFormulario = () => {
    if (crearUsuarioAdmin) {
      if (!datosAdmin.nombre || !datosAdmin.email || !datosAdmin.password) {
        setMensaje({
          type: 'error',
          text: 'Debes completar todos los datos del usuario admin'
        });
        return false;
      }

      // Validar email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(datosAdmin.email)) {
        setMensaje({
          type: 'error',
          text: 'Email inválido'
        });
        return false;
      }

      // Validar password
      if (datosAdmin.password.length < 6) {
        setMensaje({
          type: 'error',
          text: 'La contraseña debe tener al menos 6 caracteres'
        });
        return false;
      }
    }

    return true;
  };

  /**
   * Aprobar negocio
   */
  const handleAprobar = async () => {
    if (!negocio?.id) return;

    // Validar formulario
    if (!validarFormulario()) {
      return;
    }

    setProcesando(true);
    setMensaje(null);

    try {
      // 1. Aprobar el negocio (cambiar estado)
      await empresasApi.aprobarNegocio(negocio.id);

      // 2. Si hay plantilla seleccionada, aplicarla
      if (plantillaSeleccionada) {
        await configuracionApi.aplicarPlantillaByEmpresa(negocio.id, plantillaSeleccionada);
      }

      // 3. Si crear usuario admin
      if (crearUsuarioAdmin) {
        await usuariosApi.crear({
          username: datosAdmin.nombre,
          password_hash: datosAdmin.password,
          id_empresa: negocio.id,
          id_pais: negocio.id_pais,
          id_tipo_autenticacion: 1,
          id_nivel_usuario: 2, // Admin Empresa
          is_active: true
        });
      }

      // 4. Callback de éxito
      if (onAprobado) {
        onAprobado();
      }

      // 5. Cerrar dialog
      handleCerrar();

    } catch (error) {
      console.error('Error al aprobar negocio:', error);
      setMensaje({
        type: 'error',
        text: error.response?.data?.error || 'Error al aprobar negocio'
      });
    } finally {
      setProcesando(false);
    }
  };

  /**
   * Cerrar dialog
   */
  const handleCerrar = () => {
    if (!procesando) {
      setPlantillaSeleccionada('');
      setPlantillaPreview(null);
      setCrearUsuarioAdmin(true);
      setDatosAdmin({ nombre: '', email: '', password: '' });
      setMensaje(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCerrar}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ borderBottom: '2px solid #F5F5F5' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <CheckCircleIcon sx={{ mr: 2, color: '#66BB6A', fontSize: 40 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Aprobar Negocio
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {negocio?.nombre}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCerrar} disabled={procesando}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ pt: 3 }}>
        {/* Mensajes */}
        {mensaje && (
          <Alert
            severity={mensaje.type}
            onClose={() => setMensaje(null)}
            sx={{ mb: 3 }}
          >
            {mensaje.text}
          </Alert>
        )}

        {/* Resumen del negocio */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#F5F5F5', borderRadius: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Datos del Negocio
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon fontSize="small" sx={{ color: '#78909C' }} />
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    RUC
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {negocio?.ruc}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <PublicIcon fontSize="small" sx={{ color: '#78909C' }} />
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    País
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {negocio?.nombre_pais || 'No especificado'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <EmailIcon fontSize="small" sx={{ color: '#78909C' }} />
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Correo de Contacto
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {negocio?.correo_contacto}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Selección de Plantilla */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Seleccionar Plantilla de Proceso
        </Typography>

        {loadingPlantillas ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Plantilla</InputLabel>
              <Select
                value={plantillaSeleccionada}
                onChange={(e) => setPlantillaSeleccionada(e.target.value)}
                label="Plantilla"
                disabled={procesando}
              >
                <MenuItem value="">
                  <em>Sin plantilla (configuración manual)</em>
                </MenuItem>
                {plantillas.map(plantilla => (
                  <MenuItem key={plantilla.id_plantilla} value={plantilla.id_plantilla}>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {plantilla.nombre_plantilla}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {plantilla.descripcion} - {plantilla.estados_configurados?.length || 0} estados
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                La plantilla define los estados de proceso que tendrá esta empresa
              </FormHelperText>
            </FormControl>

            {/* Preview de estados si hay plantilla seleccionada */}
            {plantillaPreview && plantillaPreview.estados_configurados && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom color="textSecondary">
                  Estados que se activarán:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {plantillaPreview.estados_configurados.map(idEstado => (
                    <Chip
                      key={idEstado}
                      label={nombresEstadosDefault[idEstado] || `Estado ${idEstado}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}

        {/* Checkbox para crear usuario admin */}
        <FormControlLabel
          control={
            <Checkbox
              checked={crearUsuarioAdmin}
              onChange={(e) => setCrearUsuarioAdmin(e.target.checked)}
              disabled={procesando}
            />
          }
          label="Crear usuario administrador de empresa"
          sx={{ mt: 2 }}
        />

        {/* Formulario de usuario admin */}
        {crearUsuarioAdmin && (
          <Paper sx={{ p: 2, mt: 2, bgcolor: '#E3F2FD', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="bold">
              Datos del Usuario Admin
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre completo *"
                  value={datosAdmin.nombre}
                  onChange={(e) => setDatosAdmin({...datosAdmin, nombre: e.target.value})}
                  disabled={procesando}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email *"
                  type="email"
                  value={datosAdmin.email}
                  onChange={(e) => setDatosAdmin({...datosAdmin, email: e.target.value})}
                  disabled={procesando}
                  required
                  helperText="Email para acceder al sistema"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contraseña *"
                  type="password"
                  value={datosAdmin.password}
                  onChange={(e) => setDatosAdmin({...datosAdmin, password: e.target.value})}
                  disabled={procesando}
                  required
                  helperText="Mínimo 6 caracteres"
                />
              </Grid>
            </Grid>
          </Paper>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ borderTop: '2px solid #F5F5F5', px: 3, py: 2 }}>
        <Button
          onClick={handleCerrar}
          disabled={procesando}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleAprobar}
          variant="contained"
          disabled={procesando}
          startIcon={procesando ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          sx={{
            background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
            fontWeight: 'bold',
            px: 3,
            '&:hover': {
              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
              boxShadow: '0 4px 12px rgba(102, 187, 106, 0.4)'
            }
          }}
        >
          {procesando ? 'Aprobando...' : 'Aprobar Negocio'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogAprobarNegocio;
